import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormGroupDirective, NgForm, Validators } from '@angular/forms';
import { MatSort, MatPaginator, MatSelectChange } from '@angular/material';
import { ObservableMedia, MediaChange } from '@angular/flex-layout';

import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { first } from 'rxjs/operators/first';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { takeUntil } from 'rxjs/operators/takeUntil';
import { combineLatest } from 'rxjs/observable/combineLatest';

import { isBefore, isDate } from 'date-fns';

import { DataTableSource } from '../../classes/DataTableSource/datatablesource';

import { ApplicationService, IssueReportInfo, WebAppIssueInfo } from '../../services/application/application.service';
import { AuthService } from '../../services/authentication/auth.service';
import { DialogService } from '../../services/dialogs/dialogs.service';
import { DownloadService, DownloadFormat, HttpMethod } from '../../services/download/download.service';
import { ErrorService } from '../../services/error/error.service';
import { SplashService } from '../../services/splash/splash.service';
import { UserInfo, UserService } from '../../services/user/user.service';

import { DateFnsPipe } from '../../pipes/datefns.pipe';
import { TitleCasePipe } from '../../pipes/titlecase.pipe';

@Component({
    selector: 'app-issue-report-review',
    styleUrls: ['issuereportreview.component.css'],
    templateUrl: 'issuereportreview.component.html',
})
export class IssueReportReviewComponent implements OnInit, OnDestroy {
    @ViewChild('paginator') paginator: MatPaginator;
    @ViewChild(MatSort) sorter: MatSort;

    private _subrelease: Subject<void> = new Subject<void>();

    FilteredIssuesList: IssueReportInfo[];
    IssuesData: DataTableSource<IssueReportInfo>;
    IssuesList: IssueReportInfo[];
    AllHeaders: string[] = ['IssueNumber', 'DateReported', 'ReporterName', 'Issue', 'Status', 'ResolverName', 'Actions'];
    AvailableIssues: WebAppIssueInfo[];
    AvailableIssuesAllHeaders: string[] = ['IssueID', 'DisplayText', 'RequiresDescription', 'InUse', 'SortOrder', 'Actions'];
    AvailableIssuesVisibleHeaders: string[];
    AvailableIssuesVisibleColumns: string[];
    AvailableIssuesData: DataTableSource<WebAppIssueInfo>;
    IssueDisplayMode: string;
    NewIssueTypeForm: FormGroup;
    NoData: boolean;
    NoIssuesData: boolean;
    ViewReady: boolean;
    ShowingNewUserForm: boolean;
    SmallDisplay: boolean;
    User: UserInfo;
    VisibleHeaders: string[];
    VisibleColumns: string[];

    constructor(
        private appService: ApplicationService,
        private authService: AuthService,
        private dateFNSPipe: DateFnsPipe,
        private dialogService: DialogService,
        private downloadService: DownloadService,
        private errorService: ErrorService,
        private fb: FormBuilder,
        private mediaChange: ObservableMedia,
        private splashService: SplashService,
        private titleCasePipe: TitleCasePipe,
        private userService: UserService
    ) {}

    ngOnInit() {
        const self = this;

        self.AvailableIssues = null;
        self.AvailableIssuesData = new DataTableSource<WebAppIssueInfo>([]);
        self.FilteredIssuesList = null;
        self.IssueDisplayMode = 'U';
        self.IssuesData = new DataTableSource<IssueReportInfo>([], self.paginator, self.sorter, self.SortIssues.bind(self));
        self.IssuesList = null;
        self.NoData = false;
        self.NoIssuesData = false;
        self.ViewReady = false;
        self.ShowingNewUserForm = false;
        self.SmallDisplay = false;
        self.User = null;

        self.SmallDisplay = self.mediaChange.isActive('xs') || self.mediaChange.isActive('sm');

        self.mediaChange
            .asObservable()
            .pipe(takeUntil(self._subrelease))
            .subscribe(media => {
                self.SmallDisplay = media.mqAlias === 'xs' || media.mqAlias === 'sm';
                self.VisibleHeaders = self.SmallDisplay ? ['SmallDisplay'] : self.AllHeaders.slice();
                self.VisibleColumns = self.NoData ? ['NoData'] : self.VisibleHeaders.slice();

                self.AvailableIssuesVisibleHeaders = self.SmallDisplay ? ['SmallDisplay'] : self.AvailableIssuesAllHeaders.slice();
                self.AvailableIssuesVisibleColumns = self.NoIssuesData ? ['NoData'] : self.AvailableIssuesVisibleHeaders.slice();
            });

        // Get the first non-null UserInfo object so we can begin setting up the component
        self.userService.UserSubject.pipe(
            takeUntil(self._subrelease),
            first(user => user && !!user.AuthToken)
        ).subscribe(user => {
            self.User = user;

            // Set up constant monitoring of the user object, to catch AuthToken updates.
            self.userService.UserSubject.pipe(takeUntil(self._subrelease)).subscribe(_user => {
                self.User = _user;
            });

            // Load the issues in the database and the issue reports
            const issuereports = self.appService.GetAllIssueReports(self.User.AuthToken, self.User.CurrentRole);
            const issues = self.appService.GetAllWebAppIssues();

            combineLatest(issues, issuereports, function(_issues, reports) {
                return {
                    Issues: _issues,
                    Reports: reports,
                };
            })
                .pipe(takeUntil(self._subrelease))
                .subscribe(
                    packet => {
                        self.UpdateAvailableIssues(packet.Issues);

                        self.UpdateIssues(packet.Reports);

                        // Make the form for creating new issue types
                        self.NewIssueTypeForm = self.fb.group({
                            DisplayText: [null, Validators.required],
                            RequiresDescription: [false],
                        });

                        // Ready for component use.
                        self.ViewReady = true;
                    },
                    error => {
                        self.errorService.HandleServiceError(error);
                    }
                );
        });
    }

    ngOnDestroy() {
        this._subrelease.next();
        this._subrelease.complete();
    }

    // #region Add New Issue Type Method

    AddNewIssueType() {
        const self = this;

        const form = self.NewIssueTypeForm.value;

        // Get confirmation
        self.dialogService
            .OpenConfirmationDialog({
                TitleText: 'Confirmation Requested',
                MessageText:
                    `You are about to add the following new issue type:<br /><br />` +
                    `<strong>Display Text: </strong>${form.DisplayText}<br />` +
                    `<strong>Requires Description: </strong>${form.RequiresDescription ? 'Yes' : 'No'}<br /><br />` +
                    `Once added, the new issue type will become available upon a user's next login to the ` +
                    `DELTA System. Are you sure you wish to add this issue type?`,
                OKText: 'Yes - Add',
                CancelText: 'No - Cancel',
            })
            .subscribe(result => {
                if (result === true) {
                    // User is proceeding. Add the new issue type.
                    self.appService
                        .AddWebAppIssueType(self.User.AuthToken, self.User.CurrentRole, form.DisplayText, form.RequiresDescription)
                        .subscribe(
                            issues => {
                                // Update the types known.
                                self.UpdateAvailableIssues(issues);

                                // Clear the new issue type form.
                                self.NewIssueTypeForm.reset();

                                // Snackbar success
                                self.splashService.SnackbarSuccess('Issue Type Added Successfully.');
                            },
                            error => {
                                self.errorService.HandleServiceError(error);
                            }
                        );
                }
            });
    }

    // #endregion

    // #region Move Issue Downward Method

    MoveIssueDownward(issue: WebAppIssueInfo) {
        const self = this;

        if (!issue) {
            return;
        }

        const sortOrder = issue.SortOrder || 0;

        self.appService.UpdateWebAppIssueSortOrder(self.User.AuthToken, self.User.CurrentRole, issue.IssueID, sortOrder + 1).subscribe(
            issues => {
                // Update the types known.
                self.UpdateAvailableIssues(issues);

                // Snackbar success
                self.splashService.SnackbarSuccess('Issue Type Moved Down Successfully.');
            },
            error => {
                self.errorService.HandleServiceError(error);
            }
        );
    }

    // #endregion

    // #region Move Issue Upward Method

    MoveIssueUpward(issue: WebAppIssueInfo) {
        const self = this;

        if (!issue) {
            return;
        }

        const sortOrder = issue.SortOrder || 0;

        self.appService.UpdateWebAppIssueSortOrder(self.User.AuthToken, self.User.CurrentRole, issue.IssueID, sortOrder - 1).subscribe(
            issues => {
                // Update the types known.
                self.UpdateAvailableIssues(issues);

                // Snackbar success
                self.splashService.SnackbarSuccess('Issue Type Moved Up Successfully.');
            },
            error => {
                self.errorService.HandleServiceError(error);
            }
        );
    }

    // #endregion

    // #region Remove Issue Type Method

    RemoveIssueType(issue: WebAppIssueInfo) {
        const self = this;

        if (!issue) {
            return;
        }

        self.dialogService
            .OpenConfirmationDialog({
                TitleText: 'Confirmation Requested',
                MessageText:
                    `You are about to remove the following issue type from the Issue Reporting ` +
                    `Tool for the DELTA System:<br /><br /><strong>Issue ID #: </strong>${issue.IssueID}<br />` +
                    `<strong>Display Text: </strong>${issue.DisplayText}<br /><strong>Requires Description: </strong>` +
                    `${issue.RequiresDescription ? 'Yes' : 'No'}<br /><br />Once removed, this issue type will ` +
                    `no longer be available for use in the Issue Reporting Tool. Are you sure you wish to continue?`,
                OKText: 'Yes - Remove',
                CancelText: 'No - Cancel',
            })
            .subscribe(result => {
                if (result === true) {
                    // Remove the issue type
                    self.appService.RemoveWebAppIssueType(self.User.AuthToken, self.User.CurrentRole, issue.IssueID).subscribe(
                        issues => {
                            // Update the types known.
                            self.UpdateAvailableIssues(issues);

                            // Snackbar success
                            self.splashService.SnackbarSuccess('Issue Type Removed Successfully.');
                        },
                        error => {
                            self.errorService.HandleServiceError(error);
                        }
                    );
                }
            });
    }

    // #endregion

    // #region Resolve Issue Method

    ResolveIssue(issue: IssueReportInfo) {
        const self = this;

        if (!issue || issue.Resolved) {
            return;
        }

        const issueType = self.AvailableIssues.find(item => item.IssueID === issue.IssueID);

        const message = `You have chosen to resolve the following issue report:<br /><br />
        <strong>Issue #: </strong>${issue.WebAppIssueReportID}<br />
        <strong>Issue: </strong>${(issueType && issueType.DisplayText) || 'Other'}<br />
        <strong>Issue Description: </strong>${issue.IssueDescription}<br /><br />
        Please provide a note in the space provided below that describes the resolution. Are you sure you wish to continue?`;

        // Get a confirmation
        self.dialogService
            .OpenTextNoteDialog({ TitleText: 'Resolve Issue', MessageText: message, OKText: 'OK - Resolve', CancelText: 'Cancel' })
            .subscribe(result => {
                if (result != null) {
                    // User has opted to proceed. Signal the system to resolve this issue.
                    self.appService
                        .ResolveIssueReport(self.User.AuthToken, self.User.CurrentRole, issue.WebAppIssueReportID, result)
                        .subscribe(
                            issues => {
                                // Completed. Refresh the list of issues from what was just returned.
                                self.UpdateIssues(issues);

                                // Toast the success
                                self.splashService.SnackbarSuccess(`Issue #${issue.WebAppIssueReportID} Resolved Successfully.`);
                            },
                            error => {
                                self.errorService.HandleServiceError(error);
                            }
                        );
                }
            });
    }

    // #endregion

    // #region Sort Issues Method

    SortIssues(First: IssueReportInfo, Second: IssueReportInfo) {
        const self = this;

        let PropA: string | number | Date;
        let PropB: string | number | Date;

        switch (self.sorter.active) {
            case 'IssueNumber':
                [PropA, PropB] = [First.WebAppIssueReportID, Second.WebAppIssueReportID];
                break;
            case 'DateReported':
                [PropA, PropB] = [First.DateReported, Second.DateReported];
                break;
            case 'ReporterName':
                [PropA, PropB] = [
                    `${First.ReportedByLastName}, ${First.ReportedByFirstName}`,
                    `${Second.ReportedByLastName}, ${Second.ReportedByFirstName}`,
                ];
                break;
            case 'Issue':
                [PropA, PropB] = [First.IssueName, Second.IssueName];
                break;
            case 'Status':
                [PropA, PropB] = [First.Resolved ? 'Yes' : 'No', Second.Resolved ? 'Yes' : 'No'];
                break;
            case 'ResolverName':
                [PropA, PropB] = [
                    `${First.ResolvedByLastName}, ${First.ResolvedByFirstName}`,
                    `${Second.ResolvedByLastName}, ${Second.ResolvedByFirstName}`,
                ];
                break;
        }

        if (isDate(PropA) || isDate(PropB)) {
            let baseSort = 0;

            if (isDate(PropA) && !isDate(PropB)) {
                baseSort = 1;
            } else if (isDate(PropB) && !isDate(PropA)) {
                baseSort = -1;
            } else if (isBefore(PropA, PropB)) {
                baseSort = -1;
            } else if (isBefore(PropB, PropA)) {
                baseSort = 1;
            }

            return baseSort * (self.sorter.direction === 'asc' ? 1 : -1);
        } else {
            const ValueA = PropA != null && PropA !== '' ? (isNaN(+PropA) ? PropA : +PropA) : PropA;
            const ValueB = PropB != null && PropB !== '' ? (isNaN(+PropB) ? PropB : +PropB) : PropB;

            return (ValueA < ValueB ? -1 : 1) * (self.sorter.direction === 'asc' ? 1 : -1);
        }
    }

    // #endregion

    // #region Update Available Issues Method

    UpdateAvailableIssues(issues: WebAppIssueInfo[]) {
        const self = this;

        self.AvailableIssues = issues;

        if (!self.AvailableIssues || !self.AvailableIssues.length) {
            self.NoIssuesData = true;

            self.AvailableIssuesData.Update([{}]);
        } else {
            self.NoIssuesData = false;
            self.AvailableIssuesData.Update(self.AvailableIssues);
        }

        self.AvailableIssuesVisibleHeaders = self.SmallDisplay ? ['SmallDisplay'] : self.AvailableIssuesAllHeaders.slice();
        self.AvailableIssuesVisibleColumns = self.NoIssuesData ? ['NoData'] : self.AvailableIssuesVisibleHeaders.slice();
    }

    // #endregion

    // #region Update Issue Display Mode Method

    UpdateIssueDisplayMode(event: MatSelectChange) {
        const self = this;

        if (!event || !event.value) {
            return;
        }

        // Update the issues by triggering the filtering
        self.UpdateIssues(self.IssuesList);
    }

    // #endregion

    // #region Update Issues Method

    UpdateIssues(issues: IssueReportInfo[]) {
        const self = this;

        self.IssuesList = issues;

        // Apply filtering.
        self.FilteredIssuesList = (self.IssuesList || []).filter(item => {
            return (
                self.IssueDisplayMode === 'A' ||
                (self.IssueDisplayMode === 'U' && item.Resolved !== true) ||
                (self.IssueDisplayMode === 'R' && item.Resolved === true)
            );
        });

        if (!self.FilteredIssuesList || !self.FilteredIssuesList.length) {
            self.NoData = true;

            // Put in a fake record to trip the display message
            self.IssuesData.Update([{}]);
        } else {
            self.NoData = false;
            self.IssuesData.Update(self.FilteredIssuesList);
        }

        self.VisibleHeaders = self.SmallDisplay ? ['SmallDisplay'] : self.AllHeaders.slice();
        self.VisibleColumns = self.NoData ? ['NoData'] : self.VisibleHeaders.slice();
    }

    // #endregion

    // #region View Issue Report Detail Method

    ViewIssueReportDetail(issue: IssueReportInfo) {
        const self = this;

        let message = `<strong>Issue #: </strong>${issue.WebAppIssueReportID}<br />
        <strong>Reported On: </strong>${self.dateFNSPipe.transform(issue.DateReported, 'M/D/YYYY h:mm A')}<br />
        <strong>Reported By: </strong>${issue.ReportedByFirstName} ${issue.ReportedByLastName}<br />
        <strong>Reporter Email: </strong>${issue.ReporterEmail || 'Not Provided'}<br />
        <strong>Reporter SAPID: </strong>${issue.ReportedBySAPID || 'N/A'}<br />
        <strong>Reporter Student ID: </strong>${issue.ReportedByStudentID || 'N/A'}<br /><br />
        <strong>Issue: </strong>${issue.IssueName}<br />
        <strong>Description: </strong>${issue.IssueDescription || 'Not Provided'}<br /><br />`;

        if (issue.Resolved) {
            message += `<strong>Resolved On: </strong>${self.dateFNSPipe.transform(issue.ResolvedDate, 'M/D/YYYY h:mm A')}<br />
            <strong>Resolved By: </strong>${issue.ResolvedByFirstName} ${issue.ResolvedByLastName}<br />
            <strong>Resolver SAPID: </strong>${issue.ResolvedBySAPID}`;
        }

        self.dialogService
            .OpenMessageBoxDialog({ TitleText: `Issue #${issue.WebAppIssueReportID} Details`, MessageText: message, OKText: 'OK' })
            .subscribe();
    }

    // #endregion
}

// #region Custom Classes

// #endregion



// WEBPACK FOOTER //
// ./src/app/issuereportreview/issuereportreview.component.ts